/**
 * Configuración del Constructor de Ofertas — 1 pantalla · 6 acordeones (DEC-021).
 *
 * Orden DEC-021:
 *   1. Tipos vehículo (incluye Usos por DEC-019 "Tipos × Uso")
 *   2. Catálogo
 *   3. Condiciones
 *   4. Cuestionario
 *   5. IA
 *   6. Parámetros base (Costes operativos + Parámetros CAES)
 *
 * Endpoints de flota: /api/configuracion-constructor/...
 * Endpoints FASE 2:   /api/configuracion-fase2/...   (los editores viven en ConfiguracionEditores.jsx)
 */
import React, { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  ArrowLeft, ChevronRight, Plus, Trash2, Save, Loader2,
  Truck, Package, Tag, ClipboardList, Brain, Sliders,
} from "lucide-react";
import {
  CatalogoEditor, CondicionesEditor, CuestionarioEditor, IAEditor,
} from "./ConfiguracionEditores";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api/configuracion-constructor`;
const API_F2 = `${BACKEND_URL}/api/configuracion-fase2`;

const authAxios = axios.create();
authAxios.interceptors.request.use((cfg) => {
  const token = localStorage.getItem("mobilsafe_token");
  if (token) cfg.headers.Authorization = `Bearer ${token}`;
  return cfg;
});

export default function ConfiguracionConstructor({ onBack }) {
  const [tipos, setTipos] = useState([]);
  const [usos, setUsos] = useState([]);
  const [costes, setCostes] = useState([]);
  const [caes, setCaes] = useState(null);
  const [datosGen, setDatosGen] = useState(null);  // B4.3 · 2026-05-03
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // 7 acordeones (DEC-021 + B4.3). Solo el primero abierto por defecto.
  const [open, setOpen] = useState({
    tipos: true,
    catalogo: false,
    condiciones: false,
    cuestionario: false,
    ia: false,
    parametros: false,
    datos_generales: false,
  });
  const toggle = (k) => setOpen((s) => ({ ...s, [k]: !s[k] }));

  // IMPL-045: si llegamos con hash (#catalogo, #cuestionario, etc.) auto-expandir esa sección
  useEffect(() => {
    const h = (typeof window !== 'undefined' ? window.location.hash : '').replace('#', '');
    const validKeys = ['tipos', 'catalogo', 'condiciones', 'cuestionario', 'ia', 'parametros', 'datos_generales'];
    if (h && validKeys.includes(h)) {
      setOpen((s) => ({ ...s, [h]: true }));
      setTimeout(() => {
        const el = document.querySelector(`[data-testid="acc-${h}"]`);
        if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 300);
    }
  }, []);

  useEffect(() => { load(); }, []);

  async function load() {
    setLoading(true);
    try {
      const [t, u, c, p, dg] = await Promise.all([
        axios.get(`${API}/tipos-vehiculo`),
        axios.get(`${API}/usos-vehiculo`),
        axios.get(`${API}/costes-operativos`),
        axios.get(`${API}/parametros-caes`),
        axios.get(`${API_F2}/datos-generales`).catch(() => ({ data: null })),
      ]);
      setTipos(t.data);
      setUsos(u.data);
      setCostes(c.data);
      setCaes(p.data);
      setDatosGen(dg.data);
    } catch (e) {
      toast.error("Error cargando configuración");
    }
    setLoading(false);
  }

  // ---------- TIPOS ----------
  const setTipoCampo = (idx, campo, valor) =>
    setTipos((arr) => arr.map((t, i) => (i === idx ? { ...t, [campo]: valor } : t)));
  async function guardarTipo(t) {
    setSaving(true);
    try {
      await authAxios.put(`${API}/tipos-vehiculo/${t.id}`, {
        nombre: t.nombre, valor_unitario: Number(t.valor_unitario),
        consumo_base: Number(t.consumo_base), siniestralidad_base: Number(t.siniestralidad_base),
        coste_medio_siniestro: Number(t.coste_medio_siniestro), activo: t.activo, orden: t.orden,
      });
      toast.success(`"${t.nombre}" guardado`);
    } catch { toast.error("Error guardando tipo"); }
    setSaving(false);
  }
  async function nuevoTipo() {
    setSaving(true);
    try {
      const res = await authAxios.post(`${API}/tipos-vehiculo`, {
        nombre: "Nuevo tipo", valor_unitario: 25000, consumo_base: 8,
        siniestralidad_base: 3, coste_medio_siniestro: 2000, activo: true,
        orden: tipos.length + 1,
      });
      setTipos((arr) => [...arr, res.data]);
      toast.success("Tipo creado");
    } catch { toast.error("Error creando tipo"); }
    setSaving(false);
  }
  async function eliminarTipo(id, nombre) {
    if (!window.confirm(`¿Eliminar "${nombre}"?`)) return;
    try {
      await authAxios.delete(`${API}/tipos-vehiculo/${id}`);
      setTipos((arr) => arr.filter((t) => t.id !== id));
      toast.success("Eliminado");
    } catch { toast.error("Error eliminando"); }
  }

  // ---------- USOS ----------
  const setUsoCampo = (idx, campo, valor) =>
    setUsos((arr) => arr.map((u, i) => (i === idx ? { ...u, [campo]: valor } : u)));
  async function guardarUso(u) {
    setSaving(true);
    try {
      await authAxios.put(`${API}/usos-vehiculo/${u.id}`, {
        nombre: u.nombre, km_anio_tipico: Number(u.km_anio_tipico),
        multiplicador_consumo: Number(u.multiplicador_consumo),
        multiplicador_siniestros: Number(u.multiplicador_siniestros),
        activo: u.activo, orden: u.orden,
      });
      toast.success(`"${u.nombre}" guardado`);
    } catch { toast.error("Error guardando uso"); }
    setSaving(false);
  }
  async function nuevoUso() {
    setSaving(true);
    try {
      const res = await authAxios.post(`${API}/usos-vehiculo`, {
        nombre: "Nuevo uso", km_anio_tipico: 50000,
        multiplicador_consumo: 1.0, multiplicador_siniestros: 1.0,
        activo: true, orden: usos.length + 1,
      });
      setUsos((arr) => [...arr, res.data]);
      toast.success("Uso creado");
    } catch { toast.error("Error creando uso"); }
    setSaving(false);
  }
  async function eliminarUso(id, nombre) {
    if (!window.confirm(`¿Eliminar "${nombre}"?`)) return;
    try {
      await authAxios.delete(`${API}/usos-vehiculo/${id}`);
      setUsos((arr) => arr.filter((u) => u.id !== id));
      toast.success("Eliminado");
    } catch { toast.error("Error eliminando"); }
  }

  // ---------- COSTES ----------
  const setCosteCampo = (idx, campo, valor) =>
    setCostes((arr) => arr.map((c, i) => (i === idx ? { ...c, [campo]: valor } : c)));
  async function guardarCoste(c) {
    setSaving(true);
    try {
      await authAxios.put(`${API}/costes-operativos/${c.clave}`, {
        valor: Number(c.valor), notas: c.notas || null,
      });
      toast.success(`"${c.nombre}" guardado`);
    } catch { toast.error("Error guardando coste"); }
    setSaving(false);
  }

  // ---------- CAES ----------
  async function guardarCaes() {
    setSaving(true);
    try {
      await authAxios.put(`${API}/parametros-caes`, {
        precio_mwh: Number(caes.precio_mwh),
        factor_litro_kwh: Number(caes.factor_litro_kwh),
        comision_mobilsafe_pct: Number(caes.comision_mobilsafe_pct),
        ratio_exito_certificacion: Number(caes.ratio_exito_certificacion),
      });
      toast.success("Parámetros CAES guardados");
    } catch { toast.error("Error guardando CAES"); }
    setSaving(false);
  }

  // B4.3 (2026-05-03) — Datos Generales del Constructor (singleton)
  async function guardarDatosGenerales() {
    if (!datosGen) return;
    setSaving(true);
    try {
      await authAxios.put(`${API_F2}/datos-generales`, {
        coste_litro: Number(datosGen.coste_litro),
        pct_multas: Number(datosGen.pct_multas),
        pct_gastos_admin: Number(datosGen.pct_gastos_admin),
        pct_trabajo_admin: Number(datosGen.pct_trabajo_admin),
        pct_coste_flota_facturacion: Number(datosGen.pct_coste_flota_facturacion),
        fuente: datosGen.fuente || "",
      });
      toast.success("Datos generales guardados");
    } catch { toast.error("Error guardando datos generales"); }
    setSaving(false);
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-emerald-600 animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50" data-testid="configuracion-page">
      {/* HEADER */}
      <div className="bg-slate-900 text-white px-6 py-4 sticky top-0 z-10">
        <div className="max-w-6xl mx-auto flex items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <button onClick={onBack} data-testid="btn-volver-admin"
              className="p-2 hover:bg-white/10 rounded-lg transition">
              <ArrowLeft className="w-5 h-5" />
            </button>
            <div>
              <div className="text-xs text-slate-400 uppercase tracking-wider font-bold">Admin · DEC-021</div>
              <h1 className="text-lg font-bold">Configuración</h1>
            </div>
          </div>
          <div className="text-xs text-slate-400 hidden md:block">
            Tipos vehículo · Catálogo · Condiciones · Cuestionario · IA · Parámetros base
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-6 py-6 space-y-3">

        <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-sm text-blue-900">
          <strong>¿Dónde se usa esto?</strong> Plantillas maestras del Constructor de Ofertas. Cuando se crea una
          oferta nueva, los vehículos, productos, condiciones, cuestionario, prompts IA y costes se alimentan
          desde estos acordeones. En cada oferta se pueden sobrescribir sin afectar a estas plantillas.
        </div>

        {/* 1 · TIPOS VEHÍCULO (contiene Tipos + Usos por DEC-019 "Tipos × Uso") */}
        <Acordeon
          abierto={open.tipos}
          onToggle={() => toggle("tipos")}
          icon={<Truck className="w-4 h-4 text-emerald-600" />}
          titulo="Tipos vehículo"
          subtitulo="Categorías base + usos/trayectos que modulan consumo y siniestralidad (DEC-019)"
          count={tipos.length}
          testid="acc-tipos"
        >
          {/* Sub-sección Tipos */}
          <div className="mb-5">
            <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
              <h4 className="text-sm font-bold text-slate-700 uppercase tracking-wide">Tipos base</h4>
              <div className="flex items-center gap-2">
                {false && tipos.length > 6 && (
                  <button disabled={saving} data-testid="btn-dedupe"
                    className="px-3 py-1.5 bg-amber-100 border border-amber-300 rounded-md text-xs font-semibold hover:bg-amber-200 text-amber-800 inline-flex items-center gap-1"
                    title={`Detectados ${tipos.length} tipos · los base son 6. Esto elimina duplicados por nombre.`}>
                    Limpiar duplicados
                  </button>
                )}
                <button onClick={nuevoTipo} disabled={saving} data-testid="btn-nuevo-tipo"
                  className="px-3 py-1.5 bg-white border border-slate-300 rounded-md text-xs font-semibold hover:bg-slate-50 inline-flex items-center gap-1">
                  <Plus className="w-3 h-3" /> Añadir tipo
                </button>
              </div>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-900 text-white text-xs uppercase tracking-wide">
                  <tr>
                    <th className="text-left p-2">Nombre</th>
                    <th className="text-right p-2">Valor activo (€)</th>
                    <th className="text-right p-2">Consumo (L/100)</th>
                    <th className="text-right p-2">Siniestralidad (%)</th>
                    <th className="text-right p-2">Coste siniestro (€)</th>
                    <th className="text-left p-2">Fuente</th>
                    <th className="text-center p-2">Activo</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {tipos.map((t, idx) => (
                    <tr key={t.id} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`tipo-row-${idx}`}>
                      <td className="p-2"><Input value={t.nombre} onChange={(v) => setTipoCampo(idx, "nombre", v)} className="w-full" /></td>
                      <td className="p-2"><InputNum value={t.valor_unitario} onChange={(v) => setTipoCampo(idx, "valor_unitario", v)} /></td>
                      <td className="p-2"><InputNum value={t.consumo_base} onChange={(v) => setTipoCampo(idx, "consumo_base", v)} step="0.1" /></td>
                      <td className="p-2"><InputNum value={t.siniestralidad_base} onChange={(v) => setTipoCampo(idx, "siniestralidad_base", v)} step="0.1" /></td>
                      <td className="p-2"><InputNum value={t.coste_medio_siniestro} onChange={(v) => setTipoCampo(idx, "coste_medio_siniestro", v)} /></td>
                      <td className="p-2 text-center"><input type="checkbox" checked={t.activo} onChange={(e) => setTipoCampo(idx, "activo", e.target.checked)} /></td>
                      <td className="p-2 whitespace-nowrap">
                        <button onClick={() => guardarTipo(t)} disabled={saving} data-testid={`btn-guardar-tipo-${idx}`}
                          className="p-1.5 text-emerald-600 hover:bg-emerald-50 rounded" title="Guardar">
                          <Save className="w-4 h-4" />
                        </button>
                        <button onClick={() => eliminarTipo(t.id, t.nombre)} data-testid={`btn-eliminar-tipo-${idx}`}
                          className="p-1.5 text-red-500 hover:bg-red-50 rounded" title="Eliminar">
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Sub-sección Usos */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <h4 className="text-sm font-bold text-slate-700 uppercase tracking-wide">Usos / trayecto</h4>
              <button onClick={nuevoUso} disabled={saving} data-testid="btn-nuevo-uso"
                className="px-3 py-1.5 bg-white border border-slate-300 rounded-md text-xs font-semibold hover:bg-slate-50 inline-flex items-center gap-1">
                <Plus className="w-3 h-3" /> Añadir uso
              </button>
            </div>
            <div className="bg-amber-50 border border-amber-200 rounded-md p-2 mb-2 text-xs text-amber-900">
              Los multiplicadores se aplican sobre los valores base del tipo (DEC-019).
              Ej: Furgoneta en Urbano = consumo base 10 × 1,15 = 11,5 L/100km.
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-900 text-white text-xs uppercase tracking-wide">
                  <tr>
                    <th className="text-left p-2">Nombre</th>
                    <th className="text-right p-2">Km/año típico</th>
                    <th className="text-right p-2">Mult. consumo</th>
                    <th className="text-right p-2">Mult. siniestros</th>
                    <th className="text-left p-2">Fuente</th>
                    <th className="text-center p-2">Activo</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {usos.map((u, idx) => (
                    <tr key={u.id} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`uso-row-${idx}`}>
                      <td className="p-2"><Input value={u.nombre} onChange={(v) => setUsoCampo(idx, "nombre", v)} className="w-full" /></td>
                      <td className="p-2"><InputNum value={u.km_anio_tipico} onChange={(v) => setUsoCampo(idx, "km_anio_tipico", v)} /></td>
                      <td className="p-2"><InputNum value={u.multiplicador_consumo} onChange={(v) => setUsoCampo(idx, "multiplicador_consumo", v)} step="0.05" /></td>
                      <td className="p-2"><InputNum value={u.multiplicador_siniestros} onChange={(v) => setUsoCampo(idx, "multiplicador_siniestros", v)} step="0.05" /></td>
                      <td className="p-2">
                        <Input value={u.fuente || ""} onChange={(v) => setUsoCampo(idx, "fuente", v)}
                          className={`w-52 text-xs ${!u.fuente ? "bg-rose-50 border-rose-300" : ""}`}
                          placeholder="⚠ Sin fuente" />
                      </td>
                      <td className="p-2 text-center"><input type="checkbox" checked={u.activo} onChange={(e) => setUsoCampo(idx, "activo", e.target.checked)} /></td>
                      <td className="p-2 whitespace-nowrap">
                        <button onClick={() => guardarUso(u)} disabled={saving} data-testid={`btn-guardar-uso-${idx}`}
                          className="p-1.5 text-emerald-600 hover:bg-emerald-50 rounded" title="Guardar">
                          <Save className="w-4 h-4" />
                        </button>
                        <button onClick={() => eliminarUso(u.id, u.nombre)} data-testid={`btn-eliminar-uso-${idx}`}
                          className="p-1.5 text-red-500 hover:bg-red-50 rounded" title="Eliminar">
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </Acordeon>

        {/* 2 · CATÁLOGO */}
        <Acordeon
          abierto={open.catalogo}
          onToggle={() => toggle("catalogo")}
          icon={<Package className="w-4 h-4 text-blue-600" />}
          titulo="Catálogo de productos"
          subtitulo="HW · SaaS · Instalación · Servicios · solo lectura (se gestiona en Administración SaaS → Catálogo)"
          testid="acc-catalogo"
        >
          <CatalogoEditor />
        </Acordeon>

        {/* 3 · CONDICIONES */}
        <Acordeon
          abierto={open.condiciones}
          onToggle={() => toggle("condiciones")}
          icon={<Tag className="w-4 h-4 text-emerald-600" />}
          titulo="Condiciones comerciales"
          subtitulo="Descuentos, permanencia, comisión CAES (DEC-007)"
          testid="acc-condiciones"
        >
          <CondicionesEditor />
        </Acordeon>

        {/* 4 · CUESTIONARIO */}
        <Acordeon
          abierto={open.cuestionario}
          onToggle={() => toggle("cuestionario")}
          icon={<ClipboardList className="w-4 h-4 text-violet-600" />}
          titulo="Cuestionario consultivo"
          subtitulo="26 preguntas en 6 ejes — usado en Reuniones (DEC-001)"
          testid="acc-cuestionario"
        >
          <CuestionarioEditor />
        </Acordeon>

        {/* 5 · IA */}
        <Acordeon
          abierto={open.ia}
          onToggle={() => toggle("ia")}
          icon={<Brain className="w-4 h-4 text-amber-600" />}
          titulo="IA · Prompts y documentos plantilla"
          subtitulo="Diagnóstico, generación de oferta y contexto (DEC-009)"
          testid="acc-ia"
        >
          <IAEditor />
        </Acordeon>

        {/* 6 · PARÁMETROS BASE (Costes operativos + CAES) */}
        <Acordeon
          abierto={open.parametros}
          onToggle={() => toggle("parametros")}
          icon={<Sliders className="w-4 h-4 text-rose-600" />}
          titulo="Parámetros base"
          subtitulo="Costes operativos de referencia + Parámetros CAES"
          testid="acc-parametros"
        >
          {/* Costes */}
          <div className="mb-5">
            <h4 className="text-sm font-bold text-slate-700 uppercase tracking-wide mb-2">Costes operativos de referencia</h4>
            <table className="w-full text-sm">
              <thead className="bg-slate-100 text-xs uppercase tracking-wide text-slate-600">
                <tr>
                  <th className="text-left p-2">Concepto</th>
                  <th className="text-right p-2">Valor</th>
                  <th className="text-left p-2">Unidad</th>
                  <th className="text-left p-2">Notas</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {costes.map((c, idx) => (
                  <tr key={c.clave} className="border-b border-slate-100" data-testid={`coste-row-${idx}`}>
                    <td className="p-2 font-semibold">{c.nombre}</td>
                    <td className="p-2"><InputNum value={c.valor} onChange={(v) => setCosteCampo(idx, "valor", v)} step="0.001" /></td>
                    <td className="p-2 text-slate-500 text-xs">{c.unidad}</td>
                    <td className="p-2"><Input value={c.notas || ""} onChange={(v) => setCosteCampo(idx, "notas", v)} className="w-full text-xs" /></td>
                    <td className="p-2">
                      <button onClick={() => guardarCoste(c)} disabled={saving} data-testid={`btn-guardar-coste-${idx}`}
                        className="p-1.5 text-emerald-600 hover:bg-emerald-50 rounded">
                        <Save className="w-4 h-4" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* CAES */}
          <div>
            <h4 className="text-sm font-bold text-slate-700 uppercase tracking-wide mb-2">Parámetros CAES (DEC-020)</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <CampoCaes label="Precio energía (€/MWh)" value={caes?.precio_mwh}
                onChange={(v) => setCaes({ ...caes, precio_mwh: v })}
                hint="Variable PRECIO_MWH para cálculos CAES" />
              <CampoCaes label="Factor conversión L Diésel → kWh" value={caes?.factor_litro_kwh} step="0.1"
                onChange={(v) => setCaes({ ...caes, factor_litro_kwh: v })}
                hint="Estándar técnico (≈9,7 kWh/L)" />
              <CampoCaes label="Comisión MOBILSAFE sobre CAES (%)" value={caes?.comision_mobilsafe_pct}
                onChange={(v) => setCaes({ ...caes, comision_mobilsafe_pct: v })}
                hint="Lo que MOBILSAFE se queda del CAES certificado. Default 20%." />
              <CampoCaes label="Ratio éxito certificación (%)" value={caes?.ratio_exito_certificacion}
                onChange={(v) => setCaes({ ...caes, ratio_exito_certificacion: v })}
                hint="El cliente ve potencial total. La comisión se aplica sobre lo certificado." />
            </div>
            <div className="mt-3 flex justify-end">
              <button onClick={guardarCaes} disabled={saving} data-testid="btn-guardar-caes"
                className="px-4 py-2 bg-emerald-600 text-white rounded-lg font-semibold text-sm hover:bg-emerald-700 inline-flex items-center gap-2 disabled:opacity-50">
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Guardar parámetros CAES
              </button>
            </div>
          </div>
        </Acordeon>

        {/* 7 · DATOS GENERALES (B4.3 · 2026-05-03) */}
        <Acordeon
          abierto={open.datos_generales}
          onToggle={() => toggle("datos_generales")}
          icon={<Sliders className="w-4 h-4 text-cyan-600" />}
          titulo="Datos generales del Constructor"
          subtitulo="Valores por defecto que precarga el Constructor de Ofertas (combustible, multas, gastos administrativos…)"
          testid="acc-datos-generales"
        >
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <CampoCaes label="Coste del litro de combustible (€)" value={datosGen?.coste_litro} step="0.01"
              onChange={(v) => setDatosGen({ ...datosGen, coste_litro: v })}
              hint="Precio medio del diésel para los cálculos del Constructor" />
            <CampoCaes label="Multas (% sobre facturación / año)" value={datosGen?.pct_multas} step="0.1"
              onChange={(v) => setDatosGen({ ...datosGen, pct_multas: v })}
              hint="% del coste anual de multas sobre la facturación" />
            <CampoCaes label="Gastos administrativos (%)" value={datosGen?.pct_gastos_admin} step="0.1"
              onChange={(v) => setDatosGen({ ...datosGen, pct_gastos_admin: v })}
              hint="% gastos administrativos asociados a la flota" />
            <CampoCaes label="Trabajo administrativo (%)" value={datosGen?.pct_trabajo_admin} step="0.1"
              onChange={(v) => setDatosGen({ ...datosGen, pct_trabajo_admin: v })}
              hint="% del coste imputable al trabajo administrativo de gestión de flota" />
            <CampoCaes label="Coste flota / facturación (%)" value={datosGen?.pct_coste_flota_facturacion} step="0.1"
              onChange={(v) => setDatosGen({ ...datosGen, pct_coste_flota_facturacion: v })}
              hint="% que representa el coste de flota sobre la facturación" />
            <div>
              <label className="block text-xs font-semibold text-slate-700 uppercase tracking-wide mb-1">Fuente</label>
              <input type="text" value={datosGen?.fuente || ""}
                onChange={(e) => setDatosGen({ ...datosGen, fuente: e.target.value })}
                placeholder="Documentación de la fuente · opcional"
                data-testid="dg-fuente"
                className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:border-emerald-500" />
            </div>
          </div>
          <div className="mt-3 flex justify-end">
            <button onClick={guardarDatosGenerales} disabled={saving} data-testid="btn-guardar-datos-generales"
              className="px-4 py-2 bg-cyan-600 text-white rounded-lg font-semibold text-sm hover:bg-cyan-700 inline-flex items-center gap-2 disabled:opacity-50">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Guardar datos generales
            </button>
          </div>
        </Acordeon>

      </div>
    </div>
  );
}

// ============ Subcomponentes UI ============

function Acordeon({ abierto, onToggle, titulo, subtitulo, count, icon, children, testid }) {
  return (
    <div className="bg-white border border-slate-200 rounded-xl overflow-hidden" data-testid={testid}>
      <div onClick={onToggle} data-testid={`${testid}-header`}
        className={`px-4 py-3 cursor-pointer flex items-center justify-between transition ${abierto ? "bg-emerald-50 border-b border-emerald-200" : "bg-slate-50"}`}>
        <div className="flex items-center gap-3">
          <ChevronRight className={`w-4 h-4 text-slate-500 transition ${abierto ? "rotate-90 text-emerald-600" : ""}`} />
          {icon}
          <div>
            <div className="flex items-center gap-2">
              <h3 className="text-base font-bold text-slate-900">{titulo}</h3>
              {count !== undefined && (
                <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${abierto ? "bg-emerald-600 text-white" : "bg-slate-200 text-slate-600"}`}>{count}</span>
              )}
            </div>
            <div className="text-xs text-slate-500 mt-0.5">{subtitulo}</div>
          </div>
        </div>
      </div>
      {abierto && <div className="p-4">{children}</div>}
    </div>
  );
}

function Input({ value, onChange, className = "" }) {
  return (
    <input type="text" value={value || ""} onChange={(e) => onChange(e.target.value)}
      className={`px-2 py-1 border border-slate-300 rounded-md text-sm focus:outline-none focus:border-emerald-500 focus:ring-2 focus:ring-emerald-100 ${className}`} />
  );
}

function InputNum({ value, onChange, step = "1" }) {
  return (
    <input type="number" step={step} value={value ?? ""} onChange={(e) => onChange(e.target.value)}
      className="px-2 py-1 border border-slate-300 rounded-md text-sm w-24 text-right font-semibold focus:outline-none focus:border-emerald-500 focus:ring-2 focus:ring-emerald-100" />
  );
}

function CampoCaes({ label, value, onChange, hint, step = "1" }) {
  return (
    <div className="bg-slate-50 border border-slate-200 rounded-lg p-3">
      <div className="text-xs text-slate-600 font-semibold mb-1">{label}</div>
      <input type="number" step={step} value={value ?? ""} onChange={(e) => onChange(e.target.value)}
        data-testid={`caes-input-${label.toLowerCase().replace(/[^a-z0-9]/g, "-")}`}
        className="w-full px-3 py-2 border border-slate-300 rounded-md text-base font-bold focus:outline-none focus:border-emerald-500 focus:ring-2 focus:ring-emerald-100" />
      {hint && <div className="text-xs text-slate-400 mt-1">{hint}</div>}
    </div>
  );
}
